import { GridFilterModel, GridLogicOperator } from '@mui/x-data-grid';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export interface FilterDefinition {
  field: string;
  operator: string;
  value: unknown;
}

export const DEFAULT_FILTERS_SEARCH_PARAM_KEY = 'filter';

const useSearchParamsFilters = (
  searchParamKey = DEFAULT_FILTERS_SEARCH_PARAM_KEY,
): [FilterDefinition[], (newFilters: FilterDefinition[]) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamsFilters = useMemo(() => {
    const filters: FilterDefinition[] = [];
    if (!searchParams.has(searchParamKey)) {
      return filters;
    }
    try {
      const paramValue = JSON.parse(
        searchParams.get(searchParamKey) ?? '',
      ) as unknown;
      if (!Array.isArray(paramValue)) {
        return filters;
      }

      paramValue.forEach(filterValue => {
        if (!Array.isArray(filterValue)) {
          return;
        }
        const [field, operator, value] = filterValue as unknown[];
        if (
          typeof field !== 'string' ||
          typeof operator !== 'string' ||
          value == null
        ) {
          return;
        }
        filters.push({ field, operator, value });
      });
      return filters;
    } catch {
      return filters;
    }
  }, [searchParams, searchParamKey]);

  const setSearchParamsFilters = useCallback(
    (filters: FilterDefinition[]) => {
      setSearchParams(
        prev => {
          const searchParams = new URLSearchParams(prev);
          if (
            filters.length === 0 ||
            !filters.some(item => item.value != null)
          ) {
            if (!searchParams.has(searchParamKey)) {
              return prev;
            }
            searchParams.delete(searchParamKey);
          } else {
            const filterParam: Array<[string, string, unknown]> =
              filters.map<[string, string, unknown]>(item => [
                item.field,
                item.operator,
                item.value,
              ]) ?? [];

            searchParams.set(searchParamKey, JSON.stringify(filterParam));
          }
          return searchParams;
        },
        { replace: true },
      );
    },
    [setSearchParams, searchParamKey],
  );

  return [searchParamsFilters, setSearchParamsFilters];
};

export default useSearchParamsFilters;

export const createFilterSearchParams = (
  filters: FilterDefinition[],
  options?: { searchParamKey?: string; currentSearchParams?: URLSearchParams },
) => {
  const {
    searchParamKey = DEFAULT_FILTERS_SEARCH_PARAM_KEY,
    currentSearchParams,
  } = options ?? {};
  const searchParams = new URLSearchParams(currentSearchParams);
  if (filters.length === 0 || !filters.some(item => item.value != null)) {
    if (!searchParams.has(searchParamKey)) {
      return searchParams;
    }
    searchParams.delete(searchParamKey);
  } else {
    const filterParam: Array<[string, string, unknown]> =
      filters.map<[string, string, unknown]>(item => [
        item.field,
        item.operator,
        item.value,
      ]) ?? [];

    searchParams.set(searchParamKey, JSON.stringify(filterParam));
  }
  return searchParams;
};

export const useDataGridSearchParamsFiltersManager = (
  searchParamKey?: string,
) => {
  const [filters, setSearchParamsFilters] =
    useSearchParamsFilters(searchParamKey);

  const { initialFilterModel, onFilterModelChange } = useMemo(() => {
    const onFilterModelChange = (model: GridFilterModel) => {
      const items = model.items.filter(({ value }) => value != null);
      if (items.length === 0) {
        setSearchParamsFilters([]);
        return;
      }
      setSearchParamsFilters([
        {
          field: '',
          operator: model.logicOperator === GridLogicOperator.Or ? 'or' : 'and',
          value: '',
        },
        ...items.map<FilterDefinition>(({ field, operator, value }) => ({
          field,
          operator,
          value,
        })),
      ]);
    };
    if (!filters.length) {
      return { onFilterModelChange };
    }

    const logicOperatorFilter =
      !filters[0].field && ['or', 'and'].includes(filters[0].operator)
        ? filters[0]
        : null;

    const fieldFilters = logicOperatorFilter ? filters.slice(1) : filters;

    const initialFilterModel: GridFilterModel = {
      items: [],
      logicOperator:
        logicOperatorFilter?.operator === 'or'
          ? GridLogicOperator.Or
          : GridLogicOperator.And,
    };
    fieldFilters.forEach(({ field, operator, value }) => {
      initialFilterModel.items.push({ field, operator, value });
    });
    return { onFilterModelChange, initialFilterModel };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { initialFilterModel, onFilterModelChange };
};
